import { License } from 'renderer/features/licenses-page/schemas/licenses.schemas';
import { z } from 'zod';

const stableSlugPattern = /^[a-z0-9-]+$/;

// Get Product Details
const SaleRequestSchema = z.object({
  salePrice: z.number(),
  until: z.string(),
});

export const SupportedHostAppSchema = z.object({
  displayName: z.string(),
  id: z.string().min(1, { message: 'Host Application required' }),
  version: z.string(),
  priceOverride: z
    .union([z.number().min(0), z.literal('')])
    .optional()
    .transform((v) => (v === '' || v === undefined ? undefined : v)),
  downloadPage: z
    .union([z.string().url(), z.literal('')])
    .optional()
    .transform((v) => (v === '' || v === undefined ? undefined : v)),
});

export type HostApp = z.infer<typeof SupportedHostAppSchema>;

const SubscriptionPlanSchema = z.object({
  _id: z.string().optional(),
  planId: z.string(),
  name: z.string().optional(),
  price: z.number().min(0),
  quantity: z.number().min(0),
  isActive: z.boolean(),
  isNew: z.boolean(),
});

// export const ImageSchema = z
//   .union([
//     z.object({
//       data: z.any().optional(),
//       contentType: z.string().optional(),
//     }),
//     z.string(),
//   ])
//   .nullable()
//   .optional();

export const ProductFormSchema = z.object({
  name: z.string(),
  category: z.string(),
  keywords: z.string(),
  hidden: z.boolean().optional(),
  priority: z.boolean().optional(),
  showBadge: z.boolean().optional(),
  showInFooter: z.boolean().optional(),
  addOnId: z.string().optional(),
  priceId: z.string().optional(),
  upgradeId: z.string().optional(),
  downgradeId: z.string().optional(),
  useCustomUpgradeVersions: z.boolean().optional(),
  validUpgradeFromVersions: z.array(z.number().int().positive()).optional(),
  subscriptionPlans: z.array(SubscriptionPlanSchema).optional(),
  productId: z.string(),
  // productId: z.enum([
  //   'cloud-all-access',
  //   'cloud-one-app',
  //   'cloud-complete-bundle-all-plugins',
  //   'eff-it',
  //   'beauty-box-crossgrade',
  //   'beauty-box-photo-upgrade',
  //   'beauty-box-photo',
  //   'beauty-box-upgrade-123',
  //   'beauty-box-upgrade',
  //   'beauty-box',
  //   'beauty-extreme',
  //   'beauty-flicker',
  //   'data-storyteller-crossgrade',
  //   'data-storyteller',
  //   'flicker-free-crossgrade',
  //   'flicker-free-upgrade',
  //   'flicker-free',
  //   'flicker-free-3',
  //   'flicker-free-upgrade-1to3',
  //   'flicker-free-upgrade-2to3',
  //   'placeholder',
  //   'lightwrap-crossgrade',
  //   'lightwrap',
  //   'powersearch',
  //   'samurai-sharpen-crossgrade',
  //   'samurai-sharpen',
  //   'transcriptive-upgrade',
  //   'transcriptive',
  //   'beauty-box-6',
  //   'beauty-box-6-upgrade-1to4',
  //   'beauty-box-6-upgrade-5to6',
  // ]),
  fullPrice: z.union([z.number(), z.string()]).optional(),
  stableSlug: z
    .string()
    .min(1, 'Family name is required')
    .regex(stableSlugPattern, 'Slug must be lowercase, alphanumeric, or dash'),
  isLatest: z.boolean().optional(),
  sale: SaleRequestSchema.optional().nullable(),
  briefDescription: z.string(),
  productPage: z.string().url(),
  downloadPage: z.string().url(),
  supportedPlatforms: z.string().array(),
  supportedHostApplications: SupportedHostAppSchema.array(),
  fullDescription: z.string(),
  productImageId: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().nullable().optional(),
  ),
});

export const ProductFormResponseSchema = ProductFormSchema.extend({
  _id: z.string(),
});

export const ProductDetailsSchema = ProductFormSchema.extend({
  _id: z.string(),
  startingPrice: z.number(),
  salePrice: z.number().nullable(),
  stableSlug: z.string().nullable().optional(),
});

export const DeletedProductSchema = z.object({
  id: z.string(),
});

export const LicenseDetailsSchema = z
  .object({
    basename: z.string(),
    ids: z.string().array(),
    versions: z.string().array(),
  })
  .array();

// Read
export type GetProductDetailsResponse = z.infer<typeof ProductDetailsSchema>;
export type GetProductDetailsRequest = string;
export type ProductId = GetProductDetailsResponse['productId'];
export type GetLicenseDetailsResponse = z.infer<typeof LicenseDetailsSchema>;
export type GetLicenseDetailsRequest = void;
export type AssignLicenseRequest = {
  email: string;
  serial: string;
  removeUser?: boolean;
  resellerCode?: string;
  paymentMethodId?: string;
};
export type AssignLicenseResponse = License;
export type SupportedHostApps =
  GetProductDetailsResponse['supportedHostApplications'];

export type GetProductByPriceIdRequest = string;
export type GetProductByPriceIdResponse = GetProductDetailsResponse;
// Create
export type CreateProductResponse = z.infer<typeof ProductFormResponseSchema>;
export type CreateProductRequest = z.infer<typeof ProductFormSchema>;
// Update
export type EditProductRequest = {
  body: CreateProductRequest;
  id: string;
};
export type EditProductResponse = CreateProductResponse;
// Delete
export type DeleteProductRequest = string;
export type DeleteProductResponse = z.infer<typeof DeletedProductSchema>;

// List all products
export const ListProductsResponseSchema = ProductDetailsSchema.array();

export type ListProductsResponse = z.infer<typeof ListProductsResponseSchema>;
export type ListProductsRequest = {
  includeHidden?: boolean;
};
